import { supabase } from './supabase';
import { parseSupabaseError, withRetry, logError } from './errorHandling';
import { getCurrentSeasonYear } from '../config/season';
import type { 
  Player, 
  PlayerStats, 
  WeeklyAverage, 
  LanePerformance, 
  GamePositionStats,
  ComparisonStats
} from '../types';

/**
 * Comprehensive data fetching utilities with error handling and retry logic
 */

/**
 * Fetch all players with error handling
 */
export async function fetchPlayers(): Promise<Player[]> {
  try {
    return await withRetry(async () => {
      const { data, error } = await supabase
        .from('bowlers')
        .select('bowler_id, bowler_name, bowler_name_url_formated, gender, team_name')
        .order('bowler_name');

      if (error) {
        throw parseSupabaseError(error);
      }

      return data || [];
    });
  } catch (error) {
    logError(error as Error, { operation: 'fetchPlayers' });
    throw error;
  }
}

/**
 * Fetch player statistics with fallback methods
 */
export async function fetchPlayerStatistics(
  bowlerId: number, 
  seasonYear?: number
): Promise<PlayerStats> {
  const currentYear = seasonYear || getCurrentSeasonYear();

  try {
    return await withRetry(async () => {
      // Try database function first
      try {
        const [statsResult, weeklyResult] = await Promise.all([
          supabase.rpc('get_player_statistics', {
            target_bowler_id: bowlerId,
            season_year: currentYear,
          }),
          supabase.rpc('get_weekly_averages', {
            target_bowler_id: bowlerId,
            season_year: currentYear,
          })
        ]);

        if (statsResult.error) {
          throw parseSupabaseError(statsResult.error);
        }

        if (weeklyResult.error) {
          throw parseSupabaseError(weeklyResult.error);
        }

        const statsData = statsResult.data?.[0];
        const weeklyData = weeklyResult.data || [];

        return {
          current_average: statsData?.current_average || 0,
          total_pins: statsData?.total_pins || 0,
          total_games: statsData?.total_games || 0,
          handicap: statsData?.handicap || 0,
          high_scratch_game: statsData?.high_scratch_game || 0,
          high_scratch_series: statsData?.high_scratch_series || 0,
          high_handicap_game: statsData?.high_handicap_game || 0,
          high_handicap_series: statsData?.high_handicap_series || 0,
          most_improved: statsData?.most_improved || 0,
          weekly_averages: weeklyData.map((week: Record<string, unknown>): WeeklyAverage => ({
            week_date: week.week_date as string,
            week_number: week.week_number as number,
            average_score: week.average_score as number,
            games_played: week.games_played as number,
            total_pins: week.total_pins as number,
          })),
        };
      } catch {
        // Fallback to manual calculation
        console.warn('Database functions not available, using fallback calculation');
        return await fetchPlayerStatisticsFallback(bowlerId, currentYear);
      }
    });
  } catch (error) {
    logError(error as Error, { operation: 'fetchPlayerStatistics', bowlerId, seasonYear });
    throw error;
  }
}

/**
 * Fallback method for fetching player statistics
 */
async function fetchPlayerStatisticsFallback(
  bowlerId: number, 
  seasonYear: number
): Promise<PlayerStats> {
  const { data, error } = await supabase
    .from('league_bowler_week_stats')
    .select('*')
    .eq('bowler_id', bowlerId)
    .gte('week_date', `${seasonYear}-01-01`)
    .lte('week_date', `${seasonYear}-12-31`)
    .not('average_score', 'is', null)
    .order('week_date');

  if (error) {
    throw parseSupabaseError(error);
  }

  if (!data || data.length === 0) {
    // Return empty stats if no data found
    return {
      current_average: 0,
      total_pins: 0,
      total_games: 0,
      handicap: 0,
      high_scratch_game: 0,
      high_scratch_series: 0,
      high_handicap_game: 0,
      high_handicap_series: 0,
      most_improved: 0,
      weekly_averages: [],
    };
  }

  // Calculate statistics from raw data
  const validWeeks = data.filter(row => row.average_score && row.average_score > 0);
  const totalPins = validWeeks.reduce((sum, row) => sum + (row.total_pins || 0), 0);
  const totalGames = validWeeks.reduce((sum, row) => sum + (row.games_played || 0), 0);
  const currentAverage = totalGames > 0 ? totalPins / totalGames : 0;

  const highScratchGame = Math.max(...validWeeks.map(row => row.high_game || 0));
  const highScratchSeries = Math.max(...validWeeks.map(row => row.high_series || 0));
  const highHandicapGame = Math.max(...validWeeks.map(row => row.high_handicap_game || 0));
  const highHandicapSeries = Math.max(...validWeeks.map(row => row.high_handicap_series || 0));
  const latestHandicap = validWeeks[validWeeks.length - 1]?.handicap || 0;

  // Calculate average improvement (difference between current and entering average)
  // Note: This calculation should match the database function for consistency
  const latestAverage = validWeeks[validWeeks.length - 1]?.average_score || 0;
  const enteringAverage = validWeeks[0]?.entering_average || 0;
  const mostImproved = enteringAverage > 0 ? latestAverage - enteringAverage : 0;

  // Create weekly averages
  const weeklyAverages: WeeklyAverage[] = validWeeks.map((row, index) => ({
    week_date: row.week_date,
    week_number: index + 1, // Simple week numbering
    average_score: row.average_score || 0,
    games_played: row.games_played || 0,
    total_pins: row.total_pins || 0,
  }));

  return {
    current_average: Math.round(currentAverage * 100) / 100,
    total_pins: totalPins,
    total_games: totalGames,
    handicap: latestHandicap,
    high_scratch_game: highScratchGame,
    high_scratch_series: highScratchSeries,
    high_handicap_game: highHandicapGame,
    high_handicap_series: highHandicapSeries,
    most_improved: Math.round(mostImproved * 100) / 100,
    weekly_averages: weeklyAverages,
  };
}

/**
 * Fetch league average excluding a specific player
 */
export async function fetchLeagueAverage(
  excludedBowlerId?: number | null,
  seasonYear?: number
): Promise<number> {
  const currentYear = seasonYear || getCurrentSeasonYear();

  try {
    return await withRetry(async () => {
      // Try database function first
      try {
        const { data, error } = await supabase
          .rpc('calculate_league_average_excluding_player', {
            excluded_bowler_id: excludedBowlerId || null,
            season_year: currentYear,
          });

        if (error) {
          throw parseSupabaseError(error);
        }

        return data || 0;
      } catch {
        // Fallback to manual calculation
        console.warn('League average function not available, using fallback calculation');
        return await fetchLeagueAverageFallback(excludedBowlerId, currentYear);
      }
    });
  } catch (error) {
    logError(error as Error, { operation: 'fetchLeagueAverage', excludedBowlerId, seasonYear });
    throw error;
  }
}

/**
 * Fallback method for calculating league average
 */
async function fetchLeagueAverageFallback(
  excludedBowlerId?: number | null,
  seasonYear?: number
): Promise<number> {
  const currentYear = seasonYear || getCurrentSeasonYear();

  let query = supabase
    .from('league_bowler_week_stats')
    .select('bowler_id, average_score, games_played, total_pins')
    .gte('week_date', `${currentYear}-01-01`)
    .lte('week_date', `${currentYear}-12-31`)
    .not('average_score', 'is', null);

  if (excludedBowlerId) {
    query = query.neq('bowler_id', excludedBowlerId);
  }

  const { data, error } = await query;

  if (error) {
    throw parseSupabaseError(error);
  }

  if (!data || data.length === 0) {
    return 0;
  }

  // Calculate league average
  const playerAverages = new Map<number, { totalPins: number; totalGames: number }>();

  data.forEach((row) => {
    const current = playerAverages.get(row.bowler_id) || { totalPins: 0, totalGames: 0 };
    playerAverages.set(row.bowler_id, {
      totalPins: current.totalPins + (row.total_pins || 0),
      totalGames: current.totalGames + (row.games_played || 0),
    });
  });

  let totalPins = 0;
  let totalGames = 0;

  playerAverages.forEach((stats) => {
    totalPins += stats.totalPins;
    totalGames += stats.totalGames;
  });

  return totalGames > 0 ? Math.round((totalPins / totalGames) * 100) / 100 : 0;
}

/**
 * Fetch comprehensive player data (stats + comparisons)
 */
export async function fetchPlayerAnalysis(
  bowlerId: number,
  seasonYear?: number
): Promise<{
  player: Player;
  stats: PlayerStats;
  comparisonStats: ComparisonStats;
  lanePerformance: LanePerformance[];
  gamePositionStats: GamePositionStats[];
}> {
  try {
    // Fetch player info
    const { data: playerData, error: playerError } = await supabase
      .from('bowlers')
      .select('*')
      .eq('bowler_id', bowlerId)
      .single();

    if (playerError) {
      throw parseSupabaseError(playerError);
    }

    // Fetch all other data in parallel
    const [stats, leagueAverage] = await Promise.all([
      fetchPlayerStatistics(bowlerId, seasonYear),
      fetchLeagueAverage(bowlerId, seasonYear),
    ]);

    // Calculate comparison stats
    const percentageDifference = leagueAverage > 0 
      ? ((stats.current_average - leagueAverage) / leagueAverage) * 100 
      : 0;

    const comparisonStats: ComparisonStats = {
      player_average: stats.current_average,
      league_average: leagueAverage,
      percentage_difference: Math.round(percentageDifference * 100) / 100,
      is_above_average: stats.current_average > leagueAverage,
    };

    // For now, return empty arrays for lane and game position data
    // These would be populated by their respective hooks
    return {
      player: playerData,
      stats,
      comparisonStats,
      lanePerformance: [],
      gamePositionStats: [],
    };
  } catch (error) {
    logError(error as Error, { operation: 'fetchPlayerAnalysis', bowlerId, seasonYear });
    throw error;
  }
}

/**
 * Fetch player games data from league_bowler_week_summary
 */
export async function fetchPlayerGames(
  bowlerId: number,
  seasonYear?: number
): Promise<import('../types').PlayerGameSeries[]> {
  const currentYear = seasonYear || getCurrentSeasonYear();

  try {
    return await withRetry(async () => {
      const { data, error } = await supabase
        .from('league_bowler_week_summary')
        .select(`
          week_num,
          average_before_bowling,
          handicap_before_bowling,
          score1, score2, score3, score4, score5, score6,
          score_type1, score_type2, score_type3, score_type4, score_type5, score_type6,
          series_total,
          handicap_series_total,
          plus_minus_average,
          lane_bowled_on
        `)
        .eq('bowler_id', bowlerId)
        .eq('season_year', currentYear)
        .order('week_num');

      if (error) {
        throw parseSupabaseError(error);
      }

      if (!data || data.length === 0) {
        return [];
      }

      // Process the data to calculate averages and format games
      return data.map((row) => {
        // Count valid games (exclude absent, vacant, and zero scores)
        const validGames = [
          { score: row.score1, type: row.score_type1 },
          { score: row.score2, type: row.score_type2 },
          { score: row.score3, type: row.score_type3 },
          { score: row.score4, type: row.score_type4 },
          { score: row.score5, type: row.score_type5 },
          { score: row.score6, type: row.score_type6 },
        ].filter(game => 
          game.score > 0 && 
          game.type !== 'A' && 
          game.type !== 'V' && 
          game.type !== '0'
        );

        const gamesPlayed = validGames.length;
        const averageToday = gamesPlayed > 0 ? row.series_total / gamesPlayed : 0;
        
        // Calculate average after (before + change)
        const averageAfter = row.average_before_bowling + row.plus_minus_average;

        return {
          week_num: row.week_num,
          game1: row.score1 || 0,
          game2: row.score2 || 0,
          game3: row.score3 || 0,
          game4: row.score4 || 0,
          game5: row.score5 || 0,
          game6: row.score6 || 0,
          series_total: row.series_total,
          handicap_series_total: row.handicap_series_total,
          handicap_before_bowling: row.handicap_before_bowling,
          average_before: row.average_before_bowling,
          average_after: averageAfter,
          average_today: Math.round(averageToday * 100) / 100,
          plus_minus_average: row.plus_minus_average,
          lane_bowled_on: row.lane_bowled_on,
          games_played: gamesPlayed,
        };
      });
    });
  } catch (error) {
    logError(error as Error, { operation: 'fetchPlayerGames', bowlerId, seasonYear });
    throw error;
  }
}

/**
 * Batch fetch multiple players' basic stats
 */
export async function fetchMultiplePlayerStats(
  bowlerIds: number[],
  seasonYear?: number
): Promise<Map<number, Partial<PlayerStats>>> {
  if (bowlerIds.length === 0) {
    return new Map();
  }

  const currentYear = seasonYear || getCurrentSeasonYear();
  const results = new Map<number, Partial<PlayerStats>>();

  try {
    const { data, error } = await supabase
      .from('league_bowler_week_stats')
      .select('bowler_id, average_score, total_pins, games_played')
      .in('bowler_id', bowlerIds)
      .gte('week_date', `${currentYear}-01-01`)
      .lte('week_date', `${currentYear}-12-31`)
      .not('average_score', 'is', null);

    if (error) {
      throw parseSupabaseError(error);
    }

    // Process data for each player
    const playerData = new Map<number, { totalPins: number; totalGames: number; weekCount: number }>();

    data?.forEach((row) => {
      const current = playerData.get(row.bowler_id) || { totalPins: 0, totalGames: 0, weekCount: 0 };
      playerData.set(row.bowler_id, {
        totalPins: current.totalPins + (row.total_pins || 0),
        totalGames: current.totalGames + (row.games_played || 0),
        weekCount: current.weekCount + 1,
      });
    });

    // Calculate stats for each player
    playerData.forEach((data, bowlerId) => {
      const average = data.totalGames > 0 ? data.totalPins / data.totalGames : 0;
      results.set(bowlerId, {
        current_average: Math.round(average * 100) / 100,
        total_pins: data.totalPins,
        total_games: data.totalGames,
      });
    });

    return results;
  } catch (error) {
    logError(error as Error, { operation: 'fetchMultiplePlayerStats', bowlerIds, seasonYear });
    throw error;
  }
}